package com.bikompanion.ui.link

import com.bikompanion.ui.home.StationFocusTarget

private const val StationParameter = "station"
private const val HexDigits = "0123456789ABCDEF"
private const val MaxAsciiCode = 0x7F

/**
 * The address that opens a given station, shared by every platform: the web app's address bar, the
 * share button and the Android app link all speak `<base>?station=<networkId>/<stationId>`.
 *
 * Both ids are percent-encoded on their own and joined by a raw `/`, so an id that itself contains
 * a slash cannot be mistaken for the separator.
 */
object StationLinks {
    /**
     * Where the production web app is served, and so what a link shared from Android or desktop
     * points at. The web build shares the address it is running from instead, which keeps links
     * from a debug build or `localhost` opening on that same build.
     */
    const val ProductionBaseUrl = "https://bikompanion.com/"

    /** [baseUrl] with any query or fragment replaced by the one that selects [target]. */
    fun url(
        baseUrl: String,
        target: StationFocusTarget,
    ): String {
        val base = baseUrl.substringBefore('#').substringBefore('?')
        return "$base?$StationParameter=${target.networkId.percentEncode()}/${target.stationId.percentEncode()}"
    }

    /**
     * The station named by [url]'s query, or null when it names none or is malformed.
     *
     * Takes a whole URL or just a `?query` string, since the web app has only the latter to hand
     * and Android has the former.
     */
    fun parse(url: String): StationFocusTarget? {
        val query = url.substringAfter('?', missingDelimiterValue = "").substringBefore('#')
        val value = query
            .split('&')
            .firstOrNull { it.startsWith("$StationParameter=") }
            ?.removePrefix("$StationParameter=")
            ?: return null
        val parts = value.split('/')
        if (parts.size != 2) return null
        val networkId = parts[0].percentDecode()?.takeIf { it.isNotEmpty() } ?: return null
        val stationId = parts[1].percentDecode()?.takeIf { it.isNotEmpty() } ?: return null
        return StationFocusTarget(networkId, stationId)
    }
}

private fun Char.isUnreserved(): Boolean =
    this in 'A'..'Z' || this in 'a'..'z' || this in '0'..'9' || this == '-' || this == '.' ||
        this == '_' || this == '~'

private fun String.percentEncode(): String {
    val encoded = StringBuilder()
    for (byte in encodeToByteArray()) {
        val code = byte.toInt() and 0xFF
        val char = code.toChar()
        if (char.isUnreserved()) {
            encoded.append(char)
        } else {
            encoded.append('%')
            encoded.append(HexDigits[code shr 4])
            encoded.append(HexDigits[code and 0xF])
        }
    }
    return encoded.toString()
}

/** Null for anything this file would not have written: a bad escape, or a raw non-ASCII character. */
private fun String.percentDecode(): String? {
    val bytes = ByteArray(length)
    var count = 0
    var index = 0
    while (index < length) {
        val char = this[index]
        when {
            char == '%' -> {
                if (index + 2 >= length) return null
                bytes[count++] = (substring(index + 1, index + 3).toIntOrNull(16) ?: return null).toByte()
                index += 3
            }

            char.code > MaxAsciiCode -> {
                return null
            }

            else -> {
                bytes[count++] = char.code.toByte()
                index++
            }
        }
    }
    return bytes.decodeToString(0, count)
}
